import type {
  ISchedulerFilters,
  ISODate,
  ISchedulerPanelState,
  GroupId,
  IScheduleProfession,
  SchedulerZoom,
  SchedulerPanelTab,
  SiteId,
  TaskId,
} from '@interfaces/IScheduler';
import type { ISchedulerPlanData, IActivity } from '@components/Pages/SchedulerPage/lib/taskViewCodec';
import type { CascadeFreeze } from '@components/Pages/SchedulerPage/lib/freeze';
import type { TreeDelta } from './scheduler.delta';
import { readStickyGroupsPref } from '@utils/stickyGroupsPref';
import { readStickyLabelsPref } from '@utils/stickyLabelsPref';

/**
 * Which subtree a scoped expand/collapse-all targets — the group or task whose
 * collapse arrow was right-clicked (NOT the whole schedule).
 */
export type ScheduleSubtreeScope =
  | { kind: 'group'; groupId: GroupId }
  | { kind: 'task'; taskId: TaskId };

/** The drawer's auto-saved plan fields besides areas; `null` clears one. */
export interface ITaskPlanFields {
  profession?: IScheduleProfession | null;
  workerCount?: number | null;
}

export interface ISchedulerUiState {
  zoom: SchedulerZoom;
  /** Geometric zoom factor (1 = 100%); magnifies the whole Gantt grid. */
  zoomScale: number;
  /**
   * Scheduler-wide milestone diamond colour (CSS colour string). Display-only
   * view setting set by the auto-colorize panel — NOT a per-task field and not
   * synced to the backend. Defaults to the red token so milestones read as the
   * universal red marker until the user changes it.
   */
  milestoneColor: string;
  /**
   * Live auto-colorize preview: `taskId → '#RRGGBB'` for every non-milestone
   * task, or null when no preview is active. Display-only (the canvas overlays
   * it over the resolved colours) — NOT committed to task.color until the user
   * clicks Apply, and discarded when the panel closes. Never undoable/synced.
   */
  colorPreview: Record<TaskId, string> | null;
  /**
   * Show the in-bar progress (PPC) band on task / collapsed-summary bars.
   * Display-only view setting (default true) — hiding it lets the bar fill
   * colours read cleanly. Not undoable / not synced.
   */
  showBarProgress: boolean;
  /**
   * Display-only view setting (default false) — when on, the group and the
   * ancestor summary tasks of the topmost visible row pin (stacked) to the top
   * of the scroll area, so the outer context stays on screen while scrolling its
   * subtasks. Not undoable / not synced.
   */
  stickyGroups: boolean;
  /**
   * Display-only view setting (default true) — when on, bar labels slide along
   * their bars (sticky-left) so a task's name stays readable while horizontally
   * scrolling past its start. Not undoable / not synced.
   */
  stickyLabels: boolean;
  selectedTaskId: TaskId | null;
  selectedDependencyId: string | null;
  panelOpen: ISchedulerPanelState | null;
  panelTab: SchedulerPanelTab;
  showCriticalPath: boolean;
  filters: ISchedulerFilters;
  /** Free-text task search (toolbar). Narrows the Gantt like `filters`, by
   *  task / summary / group name; '' = off. UI-only, not synced. */
  search: string;
  /** Date range the Gantt is cut to — its timeline spans only these days and
   *  only tasks overlapping them show; null = the whole schedule. UI-only. */
  viewRange: { dateFrom: ISODate; dateTo: ISODate } | null;
  /** Measured width (px) of the visible timeline canvas — what a short
   *  `viewRange` stretches to fill (see `rangeStretch`); 0 until measured. */
  canvasWidthPx: number;
  lastError: 'cycleDetected' | 'invalidDuration' | 'requiredFieldsMissing' | null;
  /** Single-item clipboard: the id of the copied task, resolved to the live task at paste time. */
  clipboardTaskId: TaskId | null;
  /**
   * The task whose COMMIT dialog is open, or null. UI state rather than local
   * state in the row menu: a row is windowed (it unmounts as the Gantt scrolls)
   * and the dialog must outlive it — the same reason `panelOpen` lives here.
   */
  commitTaskId: TaskId | null;
  /** The row whose deletion awaits confirmation, or null (QA F-16). In the
   *  store for the same reason as `commitTaskId`: the row menu that asks is
   *  windowed, and the drawer that asks closes on the way. */
  pendingDelete: IPendingDelete | null;
}

/** A task or group deletion the user has asked for but not yet confirmed. */
export interface IPendingDelete {
  kind: 'task' | 'group';
  id: string;
}

export interface IUndoEntry {
  /**
   * What the action changed in the PLAN facet (`bySite`), by key and entity id
   * (`scheduler.delta.ts`). Never a snapshot: undo reverts exactly the fields
   * this action set, so a write saved instantly since — a status, an area, a
   * crew size — survives it (QA F-45). Plan only: no undoable action touches
   * the execution facet, which every surface writes straight through.
   */
  plan: TreeDelta;
  actionType: string;
  /** The site the action touched — undo/redo of it is frozen while that site is stale. */
  siteId?: string;
}

export interface ISchedulerHistory {
  past: IUndoEntry[];
  future: IUndoEntry[];
}

export interface ISchedulerState {
  /**
   * PLAN facet, per site: tasks without their execution fields (status,
   * progressPercent, locations, log). The whole-doc Save / import / rebase
   * own this slice; execution lives in `activityBySite`. Selectors compose the
   * two into full `TaskView`s (see `useSchedulerStore`).
   */
  bySite: Record<SiteId, ISchedulerPlanData>;
  /**
   * EXECUTION facet, per site, keyed by task id: `{ status, progressPercent,
   * locations, log }`. Written by the immediate-write surfaces (progress /
   * status / locations / lifecycle) — never by Save. A task with no entry has
   * the default execution state (`not-started`, 0 %, no areas, empty log). This
   * map is authoritative between loads; a Save response's execution half is
   * discarded (`reconcileSaved`) because the serialized PUT channel guarantees
   * server convergence.
   */
  activityBySite: Record<SiteId, Record<TaskId, IActivity>>;
  /**
   * True when the site has Genba-syncable edits not yet saved. Set by the HOR
   * for SYNCABLE actions (and undo/redo of them); cleared by bootstrap /
   * reconcileSaved. Deliberately NOT derived from history length — history is
   * capped, includes non-synced actions (collapse, markers), and survives
   * undo-to-baseline.
   */
  dirtyBySite: Record<SiteId, boolean>;
  /**
   * True when the loaded schedule is older than the upstream version (someone
   * else imported a change). While stale, ALL data edits are frozen by the HOR —
   * working on a stale base only manufactures conflicts. Cleared by bootstrap /
   * rebaseOnFresh.
   */
  staleBySite: Record<SiteId, boolean>;
  /**
   * OBSERVED work, per site, keyed by task id — the tasks the cascade must flow
   * around rather than through (`lib/freeze.ts`, and the 2026-09-20 amendment).
   * `start` pins the start date, `all` takes the task out of the cascade.
   *
   * Mirrored in from the committed-activities feed, which is TanStack Query
   * state while every `cascade` call site is a reducer. Held here rather than
   * passed per action because undo/redo would otherwise replay a stale snapshot
   * of it. A task with no entry is an ordinary plan row.
   */
  frozenBySite: Record<SiteId, Record<TaskId, CascadeFreeze>>;
  ui: ISchedulerUiState;
  history: ISchedulerHistory;
}

export const MAX_HISTORY = 50;

export const initialSchedulerState: ISchedulerState = {
  bySite: {},
  activityBySite: {},
  dirtyBySite: {},
  staleBySite: {},
  frozenBySite: {},
  ui: {
    zoom: 'week',
    zoomScale: 1,
    milestoneColor: 'var(--red)',
    colorPreview: null,
    showBarProgress: true,
    // Restored from localStorage so the pin-headers preference survives reload.
    stickyGroups: readStickyGroupsPref(),
    // Restored from localStorage so the follow-scroll-labels preference survives reload.
    stickyLabels: readStickyLabelsPref(),
    selectedTaskId: null,
    selectedDependencyId: null,
    panelOpen: null,
    panelTab: 'overview',
    showCriticalPath: false,
    filters: { status: [], groupIds: [] },
    search: '',
    viewRange: null,
    canvasWidthPx: 0,
    lastError: null,
    clipboardTaskId: null,
    commitTaskId: null,
    pendingDelete: null,
  },
  history: { past: [], future: [] },
};
